import type { WorkerJobUnit } from "@/api/adminWorker";
import { readWorkUnitTotal, type WorkUnitKind, type WorkUnitSummary } from "@/api/adminWorkUnit";

const WORK_UNIT_LABELS: Record<WorkUnitKind, string> = {
  window: "구간",
  chunk: "청크",
  job: "작업",
};

export function workUnitLabel(kind: WorkUnitKind): string {
  return WORK_UNIT_LABELS[kind];
}

// 상태별 단위 수를 수명 순서(대기 → 실행 → 완료 → 실패 → 중단)로 표시한다. 0 도 표시한다.
// 운영자는 "실패 0" 으로 실패가 없다는 사실을 확인한다.
export function formatWorkUnitCounts(summary: WorkUnitSummary): string {
  return [
    `대기 ${summary.queued}`,
    `실행 ${summary.running}`,
    `완료 ${summary.done}`,
    `실패 ${summary.failed}`,
    `중단 ${summary.cancelled}`,
  ].join(" · ");
}

// 전체 단위 수다. 추정값 앞에는 "약" 을 붙인다. 셀 수 없으면 빈 값 표기(—)다.
export function formatWorkUnitTotal(
  summary: Pick<WorkUnitSummary, "total" | "total_final">,
): string {
  const total = readWorkUnitTotal(summary);
  switch (total.state) {
    case "unknown":
      return "—";
    case "estimate":
      return `약 ${total.value}`;
    case "final":
      return String(total.value);
  }
}

// 목록 진행 열의 둘째 줄이다. 단위가 작업 전체(job)면 둘째 줄이 없다. 목록 폭은 좁다. 그래서
// 실행·실패는 0 이 아닐 때만 붙인다. 상태별 수 전체는 상세가 보인다.
export function formatWorkUnitProgress(summary: WorkUnitSummary): string | null {
  if (summary.kind === "job") return null;
  const parts = [
    `${workUnitLabel(summary.kind)} 완료 ${summary.done} / ${formatWorkUnitTotal(summary)}`,
  ];
  if (summary.running > 0) parts.push(`실행 ${summary.running}`);
  if (summary.failed > 0) parts.push(`실패 ${summary.failed}`);
  return parts.join(" · ");
}

// Worker 가 점유한 단위를 한 줄로 표시한다. 예) `구간 #3 / 약 80`, `청크 #4 / 12`, `작업 전체`.
export function formatWorkerUnit(unit: WorkerJobUnit): string {
  if (unit.kind === "job") return "작업 전체";
  const ordinal = unit.ordinal === null ? "—" : `#${unit.ordinal}`;
  return `${workUnitLabel(unit.kind)} ${ordinal} / ${formatWorkUnitTotal(unit)}`;
}
